import { useMutation } from "@tanstack/react-query";
import { useToast } from "contexts/toast/toast.context";
import { isAxiosError } from "axios";
import { useParams } from "react-router-dom";
import { useNavigate, useLocation } from "react-router";
import { getAppointmentConfirmLink } from "api/client/get-appointment-confirm-link";
import { getOneTimeAuthLink } from "api/client/get-one-time-auth-link";
import { getRecommendationPublicLink } from "api/client/get-recommendation-public-link";
import { getRecommendationUserLink } from "api/client/get-recommendation-user-link";
import { getAppointmentOnlinePaymentLink } from "api/client/get-appointment-online-payment-link";
import { getAppointmentCalendarLink } from "api/client/get-appointment-calendar-link";

type ApiFn = (args: { code: string }) => Promise<{ returnCode: number; data: { link: string } }>;

type HandlerOptions = {
    // true redirects the whole browser (window.location.href) instead of navigating within the SPA
    external?: boolean;
    // shown instead of the "Trwa przekierowywanie..." text once an external redirect succeeds,
    // since window.location.href doesn't necessarily leave the page (e.g. a file download or
    // opening a native app) and the loading text would otherwise linger indefinitely
    successMessage?: string;
};

// Longer prefixes must come first to avoid false-positive matches
const PATH_HANDLERS: Array<[string, ApiFn, HandlerOptions?]> = [
    ['/zalecenia/uzytkownik', getRecommendationUserLink],
    ['/zalecenia', getRecommendationPublicLink],
    ['/zaproszenie', getOneTimeAuthLink],
    ['/wizyta', getAppointmentConfirmLink],
    ['/platnosc', getAppointmentOnlinePaymentLink],
    ['/kalendarz', getAppointmentCalendarLink, {
        external: true,
        successMessage: "Wydarzenie zostało dodane do kalendarza. Możesz zamknąć tę stronę.",
    }],
];

export default function useRedirectLink() {
    const { addFailureToast } = useToast();
    const { code } = useParams<{ code: string }>();
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const handler = PATH_HANDLERS.find(([prefix]) => pathname.startsWith(prefix));
    const apiFn = handler?.[1];
    const options = handler?.[2];

    const { mutate, isPending, isError, isSuccess } = useMutation({
        mutationFn: () => {
            if (!apiFn) return Promise.reject(new Error('Unknown redirect path'));
            return apiFn({ code });
        },
        onSuccess: (data) => {
            if (data?.data?.link) {
                if (options?.external) {
                    window.location.href = data.data.link;
                } else {
                    navigate(`/${data.data.link}`);
                }
            }
        },
        onError: (err) => {
            let message = "Wystąpił niespodziewany błąd.";
            if (isAxiosError(err)) {
                message = err.response?.data?.message || err.message || message;
            } else {
                message = err.message || message;
            }
            addFailureToast(message);
        },
    });

    return {
        mutate,
        isPending,
        isError,
        successMessage: isSuccess && options?.external ? options.successMessage : undefined,
    };
}
